'use client';

/**
 * CategoryCard Component
 *
 * A card component for displaying service categories with an image and name.
 * Can display different visual variants (e.g., default, rainbow).
 */
import Image from 'next/image';
import React, { useEffect } from 'react';

import { cn } from '@ui/lib/utils';

// Inject keyframes once
let stylesInjected = false;
function injectClickAnimationStyles() {
  if (stylesInjected || typeof document === 'undefined') return;

  const styleId = 'category-card-click-animation';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes categoryShimmer {
      0%, 100% { 
        opacity: 0.3;
        filter: brightness(1);
      }
      50% { 
        opacity: 0.7;
        filter: brightness(1.4);
      }
    }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

export interface CategoryCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /**
   * Category ID - used for tracking (optional)
   */
  categoryId?: string;

  /**
   * Whether this card is currently animating
   */
  isAnimating?: boolean;

  /**
   * Callback when card is clicked (for animation control)
   */
  onCardClick?: () => void;

  /**
   * Category name to display
   */
  name: string;

  /**
   * Image URL for the category (required)
   */
  imageUrl: string;

  /**
   * Visual variant of the card
   * @default 'default'
   */
  variant?: 'default' | 'rainbow' | 'active';

  /**
   * Whether the image should be considered high priority and preloaded.
   * Useful for LCP images.
   * @default true
   */
  priority?: boolean;

  /**
   * Additional className
   */
  className?: string;

  /**
   * Custom onClick handler (in addition to onCardClick)
   */
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * CategoryCard component for displaying service categories.
 * The 'rainbow' variant applies a special gradient border.
 *
 * @example
 * ```tsx
 * <CategoryCard
 *   name="Health & Wellness"
 *   imageUrl="https://example.com/health.jpg"
 * />
 *
 * <CategoryCard
 *   name="Discover All Skills"
 *   imageUrl="/assets/images/all_categories.png"
 *   variant="rainbow"
 * />
 * ```
 */
export const CategoryCard = ({
  categoryId,
  isAnimating = false,
  onCardClick,
  name,
  imageUrl,
  variant = 'default',
  priority = true,
  className,
  style,
  ...props
}: CategoryCardProps) => {
  useEffect(() => {
    injectClickAnimationStyles();
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Trigger animation callback if provided
    if (onCardClick) {
      onCardClick();
    }

    // Call the original onClick if provided (for Link navigation)
    if (props.onClick) {
      props.onClick(e);
    }
  };

  const handleMouseDown = () => {
    // Trigger animation on mouse down for immediate feedback
    if (onCardClick) {
      onCardClick();
    }
  };

  return (
    <div
      {...props}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      className={cn(
        // Base styles
        'w-full relative bg-background cursor-pointer rounded-lg overflow-hidden px-2 py-3 md:px-4 md:py-5 aspect-[2.4/1] md:max-w-[400px]',
        // Responsive styles
        'min-h-[92px] @[780px]:min-w-[230px]',
        // Transition effects
        'transition-all duration-200 ease-in-out',
        // Variant-specific styles
        variant === 'default'
          ? 'border-1 border-neutral-200 hover:border-neutral-400'
          : variant === 'active'
            ? 'border-orange-500 border-2'
            : 'hover:brightness-95',
        // Scale animation on click
        isAnimating && 'scale-[0.98]',
        className
      )}
      style={{
        ...style,
        ...(variant === 'rainbow'
          ? {
              border: '2px solid transparent',
              backgroundImage:
                'linear-gradient(white, white), linear-gradient(to right, #C00AD0, #F8CAA7, #F58879, #ED6804)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              outline: 'none'
            }
          : {})
      }}
    >
      {/* Category name */}
      <div className="h-full flex items-center mr-28">
        <div className="text-foreground text-sm font-semibold leading-tight z-20 text-left">{name}</div>
      </div>

      {/* Category image */}
      <div className="h-full aspect-[1/1] absolute top-0 right-0 overflow-hidden">
        <Image
          src={imageUrl}
          alt={name}
          fill={true}
          sizes="(max-width: 768px) 120px, 160px"
          quality={60}
          style={{ objectFit: 'cover' }}
          className="h-full w-auto z-10"
          priority={priority}
        />
      </div>

      {/* Shimmer animation overlay - continuous until navigation */}
      {isAnimating && (
        <div
          className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg z-10"
          style={{
            background: 'rgba(255, 255, 255, 0.5)',
            animation: 'categoryShimmer 1.5s ease-in-out infinite'
          }}
        />
      )}
    </div>
  );
};
